import { useCallback, useRef, useState } from 'react'

import { PICKER_TEXT } from '../constants'
import { Overlay } from '../Overlay/Overlay'
import { PLACEMENT_CLASS_NAMES } from '../Overlay/positionUtils'
import { PickerToggle } from '../Picker/PickerToggle'
import { useFocusItemValue } from '../Picker/useFocusItemValue'
import { useSearch } from '../Picker/useSearch'
import { useToggleKeyDownEvent } from '../Picker/useToggleKeyDownEvent'
import { PickerItem } from '../types'
import { mergeRefs } from '../utils/mergeRefs'
import { useClassNames } from '../utils/useClassNames'
import { useControlled } from '../utils/useControlled'
import { usePortal } from '../utils/usePortal'
import { BaseCheckPickerProps } from './CheckPicker.types'
import { CheckPickerMenu } from './CheckPickerMenu'
import { getSelectedDisplay } from './getSelectedDisplay'
import { useCheckPickerSelection } from './useCheckPickerSelection'

export const BaseCheckPicker = <Item extends PickerItem>({
  data = [],
  value: valueProp,
  defaultValue,
  placeholder,
  placement = 'bottomStart',
  className,
  block,
  cleanable = true,
  disabled,
  searchable = true,
  keepSearchFocused = false,
  sticky,
  groupBy,
  defaultOpen,
  preventOverflow = false,
  container,
  menuClassName,
  menuStyle,
  toggleRef,
  searchBy,
  renderMenu,
  renderMenuItem,
  renderMenuGroup,
  renderValue,
  onChange,
  onSelect,
  onSearch,
  onClean,
  onOpen,
  onClose,
  onEnter,
  onExit,
}: BaseCheckPickerProps<Item>) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const targetRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const Portal = usePortal(container)
  const { merge, prefix, withClassPrefix } = useClassNames('picker')

  const [open, setOpen] = useState(Boolean(defaultOpen))
  const [active, setActive] = useState(false)
  const [stickyItems, setStickyItems] = useState<Item[]>([])
  const [value, setValue] = useControlled({
    controlledValue: valueProp,
    defaultValue: defaultValue || [],
  })
  const selectedValues = value ?? []

  const getMenu = useCallback(() => overlayRef.current, [])
  const {
    focusItemValue,
    setFocusItemValue,
    onKeyDown: onFocusItemKeyDown,
  } = useFocusItemValue({ defaultFocusItemValue: selectedValues[0], data, getMenu })

  const { searchKeyword, setSearchKeyword, checkShouldDisplay, handleSearch } = useSearch({
    data,
    searchBy,
    onSearchResult: ({ keyword, filteredData, event }) => {
      setFocusItemValue(filteredData[0]?.value)
      onSearch?.(keyword, event)
    },
  })

  const closeOverlay = useCallback(() => setOpen(false), [])

  const toggleMenu = () => (open ? closeOverlay() : setOpen(true))

  const closeMenu = () => {
    setOpen(false)
    setFocusItemValue(null)
  }

  const { handleClean, handleMenuPressEnter, handleItemSelect } = useCheckPickerSelection({
    data,
    selectedValues,
    setValue,
    focusItemValue,
    setFocusItemValue,
    disabled,
    cleanable,
    onChange,
    onSelect,
    onClean,
  })

  const handlePickerKeyDown = useToggleKeyDownEvent({
    toggle: !focusItemValue || !active,
    active,
    targetRef,
    overlayRef,
    searchInputRef,
    openMenu: () => setOpen(true),
    closeMenu,
    onTargetBackspace: onExit ? () => onExit(null) : handleClean,
    onMenuKeyDown: onFocusItemKeyDown,
    onMenuPressEnter: handleMenuPressEnter,
    onMenuPressBackspace: handleClean,
  })

  const handleEnter = (node: HTMLElement | null) => {
    if (sticky) {
      setStickyItems(
        selectedValues.length
          ? data.filter(item => selectedValues.some(selectedValue => selectedValue === item.value))
          : [],
      )
    }
    onEnter?.(node)
  }

  const handleEntered = () => {
    setActive(true)
    onOpen?.()
  }

  const handleExited = () => {
    setSearchKeyword('')
    setFocusItemValue(null)
    setActive(false)
    onClose?.()
  }

  const selectedItems = data.filter(item =>
    selectedValues.some(selectedValue => Object.is(item.value, selectedValue)),
  )
  const { selectedElement, hasValue } = getSelectedDisplay({
    selectedValues,
    selectedItems,
    placeholder,
    prefix,
    renderValue,
  })

  const rootClassName = merge(
    className,
    withClassPrefix('check', 'default', 'toggle-wrapper', {
      [PLACEMENT_CLASS_NAMES[placement]]: true,
      'has-value': hasValue,
      cleanable: hasValue && cleanable,
      block,
      disabled,
      countable: true,
    }),
  )

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div ref={rootRef} className={rootClassName} onClick={disabled ? undefined : toggleMenu}>
        <PickerToggle
          ref={mergeRefs(targetRef, toggleRef)}
          disabled={disabled}
          cleanable={cleanable && !disabled}
          hasValue={hasValue}
          active={active}
          placement={placement}
          onClean={handleClean}
          onKeyDown={handlePickerKeyDown}
        >
          {selectedElement || PICKER_TEXT.placeholder}
        </PickerToggle>
      </div>
      <Portal>
        <Overlay
          open={open}
          container={container}
          placement={placement}
          preventOverflow={preventOverflow}
          triggerTarget={rootRef}
          onClose={closeOverlay}
          onEnter={handleEnter}
          onEntered={handleEntered}
          onExit={onExit}
          onExited={handleExited}
        >
          {(positionProps, positionRef) => (
            <CheckPickerMenu
              positionProps={positionProps}
              positionRef={positionRef}
              overlayRef={overlayRef}
              triggerRef={rootRef}
              searchInputRef={searchInputRef}
              data={data}
              stickyItems={stickyItems}
              selectedValues={selectedValues}
              focusItemValue={focusItemValue}
              groupBy={groupBy}
              searchable={searchable}
              keepSearchFocused={keepSearchFocused && open}
              searchKeyword={searchKeyword}
              menuClassName={menuClassName}
              menuStyle={menuStyle}
              isDisplayed={checkShouldDisplay}
              renderMenu={renderMenu}
              renderMenuItem={renderMenuItem}
              renderMenuGroup={renderMenuGroup}
              onSearch={handleSearch}
              onKeyDown={handlePickerKeyDown}
              onSelect={handleItemSelect}
            />
          )}
        </Overlay>
      </Portal>
    </>
  )
}
